import type { ReactNode } from 'react';
import Box from '@mui/material/Box';
import { NARROW_QUERY, SCROLL_REGION_SX, SURFACE_SX } from './layoutStyles';
import { PageFrame, type PageFrameProps } from './PageFrame';
import { WS_FRAME } from './tokens';

export interface RecordLayoutProps extends Pick<PageFrameProps, 'header' | 'tabs' | 'fill'> {
  /** The active tab's content: `<Section>`s. Scrolls on its own. */
  children: ReactNode;
  /** Facts rail: owner, status, dates, key numbers. Scrolls on its own. */
  facts?: ReactNode;
  /** Facts rail label for assistive technology. */
  factsLabel?: string;
}

/**
 * LAYOUT 3 — Record workspace (DESIGN.md): an identity header (`PageHeader` with `id` and
 * status in `meta`), tabs, the record's sections beside a facts rail.
 *
 * Wide: content and rail scroll independently, so the facts stay in view while a long
 * section is read. Narrow (899px or less of layout width): one column, the rail first as an
 * overview, and the whole body is the one scroll region (scrolled to its top).
 */
export function RecordLayout({
  header,
  tabs,
  children,
  facts,
  factsLabel,
  fill,
}: RecordLayoutProps) {
  return (
    <PageFrame header={header} tabs={tabs} fill={fill}>
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          minHeight: 0,
          display: 'grid',
          gap: 4,
          gridTemplateColumns: facts ? `minmax(0, 1fr) ${WS_FRAME.paneWidth}px` : 'minmax(0, 1fr)',
          gridTemplateRows: 'minmax(0, 1fr)',
          [NARROW_QUERY]: {
            display: 'flex',
            // `order`, not `column-reverse`: a reversed flex container starts scrolled to its END.
            flexDirection: 'column',
            overflowY: 'auto',
            gap: 4,
            '& > *': { overflow: 'visible', flex: 'none' },
            '& > aside': { order: -1 },
          },
        }}
      >
        <Box tabIndex={0} sx={{ ...SURFACE_SX, ...SCROLL_REGION_SX, p: 5 }}>
          {children}
        </Box>
        {facts ? (
          <Box
            component="aside"
            aria-label={factsLabel ?? 'Facts'}
            tabIndex={0}
            sx={{ ...SURFACE_SX, ...SCROLL_REGION_SX, p: 4 }}
          >
            {facts}
          </Box>
        ) : null}
      </Box>
    </PageFrame>
  );
}
